import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import api, { formatApiErrorDetail } from "@/lib/api";
import { RegimeBadge, ScoreGauge, ProbBar, Change, Loader, DataTag, Empty, AmountChips } from "@/components/widgets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Gate } from "@/components/Gate";
import { AssetLogo } from "@/components/AssetLogo";
import { CheckCircle, Warning, XCircle, Target, Sparkle, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import useAlpacaTicks from "@/hooks/useAlpacaTicks";

export default function StockDetail() {
  const { ticker } = useParams();
  const live = useAlpacaTicks(ticker);
  const [intel, setIntel] = useState(null);
  const [hist, setHist] = useState([]);
  const [news, setNews] = useState([]);
  const [setup, setSetup] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(() => {
    setIntel(null); setErr(null);
    api.get(`/intelligence/${ticker}`).then((r) => setIntel(r.data)).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail)));
    api.get(`/history/${ticker}?period=6mo`).then((r) => setHist(r.data.data)).catch(() => {});
    api.get(`/news/${ticker}`).then((r) => setNews(r.data.articles)).catch(() => {});
    api.get(`/setups/${ticker}`).then((r) => setSetup(r.data)).catch(() => {});
  }, [ticker]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="border border-signal-red/40 bg-signal-red/5 p-6 mono text-sm text-signal-red">{err}</div>;
  if (!intel) return <Loader label={`Analyzing ${ticker}`} />;

  const dirColor = intel.direction === "bullish" ? "#34C759" : intel.direction === "bearish" ? "#FF3B30" : "#007AFF";

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <AssetLogo ticker={intel.ticker} size={40} />
            <h1 className="font-heading text-4xl font-bold tracking-tighter">{intel.ticker}</h1>
            <RegimeBadge regime={intel.regime} />
          </div>
          <div className="mt-1 text-muted-foreground">{intel.name} · <span className="mono text-xs">{intel.sector}</span></div>
        </div>
        <div className="text-right">
          <div className="mono text-3xl font-semibold">${live.price ?? intel.price}</div>
          {live.feed && (
            <div className="mono mt-1 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border text-signal-green border-signal-green/40 bg-signal-green/10" data-testid="live-badge" title={live.bid != null ? `Bid ${live.bid} · Ask ${live.ask}` : "Waiting for ticks"}>
              <span className="live-dot inline-block h-1.5 w-1.5 rounded-full bg-signal-green" />Live · {live.feed === "sip" ? "SIP" : "IEX"}
            </div>
          )}
          <Change value={intel.change_pct} />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Chart + score */}
        <div className="lg:col-span-2 border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-4">
            <span className="label-xs">Price · 6M <span className="text-white/40">(FACT — realized)</span></span>
            <DataTag kind="FACT" />
          </div>
          <div className="h-[300px] min-h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <LineChart data={hist} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8A8A93" }} tickFormatter={(d) => d?.slice(5)} minTickGap={40} />
                <YAxis domain={["auto", "auto"]} tick={{ fontSize: 10, fill: "#8A8A93" }} width={50} />
                <Tooltip contentStyle={{ background: "#141415", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 4, fontSize: 12, fontFamily: "IBM Plex Mono" }} />
                <Line type="monotone" dataKey="close" stroke="#fff" strokeWidth={1.6} dot={false} name="Close" />
                <Line type="monotone" dataKey="sma20" stroke="#007AFF" strokeWidth={1.2} dot={false} name="SMA20" />
                <Line type="monotone" dataKey="sma50" stroke="#FFCC00" strokeWidth={1} strokeDasharray="4 3" dot={false} name="SMA50" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Score card */}
        <div className="border border-border bg-card p-5 flex flex-col items-center">
          <div className="w-full flex items-center justify-between mb-2"><span className="label-xs">Intelligence</span><DataTag kind="AI" /></div>
          <ScoreGauge score={intel.score} direction={intel.direction} />
          <div className="mt-4 grid grid-cols-3 w-full text-center gap-2">
            <MiniStat label="Direction" value={intel.direction} cls={`uppercase`} color={dirColor} />
            <MiniStat label="Confidence" value={intel.confidence} />
            <MiniStat label="Risk" value={intel.risk_level} cls="uppercase" />
          </div>
          <p className="mt-4 text-xs text-muted-foreground text-center border-t border-border pt-3">{intel.regime_description}</p>
        </div>
      </div>

      {/* Forecast */}
      <Gate need="pro" compact>
      <div className="mt-4 border border-border bg-card p-5">
        <div className="flex items-center justify-between mb-4">
          <span className="label-xs">Probabilistic Forecast</span>
          <div className="flex items-center gap-2"><DataTag kind="MODEL" /></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {["1d", "5d", "20d"].map((h) => {
            const f = intel.forecast[h];
            return (
              <div key={h} className="border border-dashed border-white/15 p-4" data-testid={`forecast-${h}`}>
                <div className="flex items-center justify-between">
                  <span className="mono text-sm font-semibold">{h.toUpperCase()} horizon</span>
                  <span className="mono text-xs text-muted-foreground">conf {f.confidence}</span>
                </div>
                <div className="my-3"><ProbBar bull={f.prob_bull} neutral={f.prob_neutral} bear={f.prob_bear} /></div>
                <div className="grid grid-cols-2 gap-2 mono text-xs">
                  <div><div className="label-xs">Expected</div><div>${f.expected_price} <span className={f.expected_return_pct >= 0 ? "pos" : "neg"}>({f.expected_return_pct >= 0 ? "+" : ""}{f.expected_return_pct}%)</span></div></div>
                  <div className="text-right"><div className="label-xs">80% Range</div><div>${f.range_low}–${f.range_high}</div></div>
                </div>
                <div className="mt-2 label-xs">Hist. dir. accuracy: <span className="mono text-white">{(f.historical_directional_accuracy * 100).toFixed(0)}%</span></div>
              </div>
            );
          })}
        </div>
        <p className="mt-4 mono text-[11px] text-muted-foreground">Model output — probabilistic, not a prediction or guarantee of returns.</p>
      </div>
      </Gate>

      {/* AI Deep Dive */}
      <Gate need="pro" compact>
        <AIDeepDive ticker={intel.ticker} />
      </Gate>

      {/* Factors + Setup + News */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-3"><span className="label-xs">Key & Risk Factors</span><DataTag kind="AI" /></div>
          <FactorList items={intel.key_factors} icon={CheckCircle} color="text-signal-green" />
          <div className="my-3 border-t border-border" />
          <FactorList items={intel.risk_factors} icon={Warning} color="text-signal-yellow" />
          <div className="my-3 border-t border-border" />
          <div className="label-xs mb-2">Invalidation Conditions</div>
          <FactorList items={intel.invalidation_conditions} icon={XCircle} color="text-signal-red" />
        </div>

        {/* Trade setup + order ticket */}
        <div className="border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-3"><span className="label-xs flex items-center gap-1.5"><Target size={13} /> Trade Setup</span><DataTag kind="HYPO" /></div>
          {setup && <TradeSetup setup={setup} />}
          <OrderTicket ticker={intel.ticker} price={intel.price} onDone={load} />
        </div>

        {/* Risk metrics + news */}
        <div className="border border-border bg-card p-5">
          <Tabs defaultValue="risk">
            <TabsList className="bg-background rounded-sm">
              <TabsTrigger value="risk" data-testid="tab-risk">Risk</TabsTrigger>
              <TabsTrigger value="news" data-testid="tab-news">News</TabsTrigger>
            </TabsList>
            <TabsContent value="risk" className="mt-4 space-y-2">
              {Object.entries(intel.risk_metrics).map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-border/50 py-2 text-sm">
                  <span className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}</span>
                  <span className="mono">{v}{k.includes("volatility") || k.includes("drawdown") || k.includes("risk") ? "%" : ""}</span>
                </div>
              ))}
            </TabsContent>
            <TabsContent value="news" className="mt-4">
              {news.length === 0 ? <Empty>No recent news</Empty> : news.slice(0, 6).map((n, i) => (
                <a key={i} href={n.url} target="_blank" rel="noreferrer" className="block border-b border-border/50 py-2.5 hover:bg-white/[0.03] transition-colors duration-200 px-1">
                  <div className="flex items-start gap-2">
                    <span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${n.sentiment === "positive" ? "bg-signal-green" : n.sentiment === "negative" ? "bg-signal-red" : "bg-muted-foreground"}`} />
                    <div><div className="text-xs leading-snug">{n.title}</div><div className="label-xs mt-1">{n.publisher}</div></div>
                  </div>
                </a>
              ))}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

const CONVICTION = {
  high: "text-signal-green border-signal-green/40 bg-signal-green/10",
  medium: "text-signal-yellow border-signal-yellow/40 bg-signal-yellow/10",
  low: "text-muted-foreground border-border bg-white/5",
};

function AIDeepDive({ ticker }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const run = useCallback(async (refresh = false) => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get(`/ai/deepdive/${ticker}${refresh ? "?refresh=true" : ""}`);
      setData(r.data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not generate analysis.");
    } finally { setLoading(false); }
  }, [ticker]);

  const a = data?.analysis;

  return (
    <div className="mt-4 border border-volt/30 bg-volt/[0.03] ai-glow p-5" data-testid="ai-deepdive">
      <div className="flex items-center justify-between mb-1">
        <span className="label-xs flex items-center gap-1.5"><Sparkle size={14} className="text-volt" weight="fill" /> AI Deep Dive</span>
        <div className="flex items-center gap-2">
          {data && <span className="mono text-[10px] text-muted-foreground uppercase" data-testid="ai-deepdive-model">{data.model}</span>}
          <DataTag kind="AI" />
        </div>
      </div>

      {!data && !loading && (
        <div className="py-6 text-center">
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Have our reasoning model read {ticker}'s signals, forecast and latest news, then lay out the bull case, bear case and what would flip the thesis.
          </p>
          <Button data-testid="ai-deepdive-generate" onClick={() => run(false)}
            className="mt-4 bg-volt hover:bg-volt/90 text-white rounded-sm gap-2">
            <Sparkle size={16} weight="fill" /> Generate AI Deep Dive
          </Button>
          {err && <p className="mt-3 mono text-xs text-signal-red">{err}</p>}
        </div>
      )}

      {loading && (
        <div className="py-10 flex flex-col items-center text-muted-foreground" data-testid="ai-deepdive-loading">
          <div className="mono text-xs tracking-widest animate-pulse">Reading signals &amp; latest news…</div>
          <div className="mt-3 h-px w-40 overflow-hidden bg-white/10">
            <div className="h-full w-1/3 bg-volt animate-[loading_1.2s_ease-in-out_infinite]" />
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground/70">Reasoning models take a few seconds.</p>
        </div>
      )}

      {a && !loading && (
        <div className="mt-2" data-testid="ai-deepdive-result">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-heading text-lg font-semibold">{a.headline}</h3>
            {a.conviction && (
              <span data-testid="ai-conviction" className={`mono text-[11px] uppercase tracking-wide px-2 py-1 rounded-sm border ${CONVICTION[a.conviction] || CONVICTION.low}`}>
                {a.conviction} conviction
              </span>
            )}
          </div>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{a.summary}</p>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-signal-green/20 bg-signal-green/[0.04] p-3 rounded-sm">
              <div className="label-xs text-signal-green mb-2">Bull case</div>
              <ul className="space-y-1.5">{(a.bull_case || []).map((x, i) => (
                <li key={i} className="flex items-start gap-2 text-xs"><CheckCircle size={14} className="mt-0.5 shrink-0 text-signal-green" /><span className="text-muted-foreground">{x}</span></li>
              ))}</ul>
            </div>
            <div className="border border-signal-red/20 bg-signal-red/[0.04] p-3 rounded-sm">
              <div className="label-xs text-signal-red mb-2">Bear case</div>
              <ul className="space-y-1.5">{(a.bear_case || []).map((x, i) => (
                <li key={i} className="flex items-start gap-2 text-xs"><XCircle size={14} className="mt-0.5 shrink-0 text-signal-red" /><span className="text-muted-foreground">{x}</span></li>
              ))}</ul>
            </div>
          </div>

          {a.catalysts?.length > 0 && (
            <div className="mt-4">
              <div className="label-xs mb-2">Catalysts to watch</div>
              <div className="flex flex-wrap gap-1.5">
                {a.catalysts.map((c, i) => (
                  <span key={i} className="mono text-[11px] px-2 py-1 rounded-sm border border-border text-muted-foreground">{c}</span>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
            <div><div className="label-xs mb-1">News takeaway</div><p className="text-muted-foreground leading-relaxed">{a.news_takeaway}</p></div>
            <div><div className="label-xs mb-1 text-signal-yellow">Invalidation</div><p className="text-muted-foreground leading-relaxed">{a.invalidation}</p></div>
          </div>

          {a.conviction_rationale && (
            <p className="mt-3 text-[11px] text-muted-foreground/80 italic">{a.conviction_rationale}</p>
          )}

          <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
            <span className="mono text-[10px] text-muted-foreground">{data.disclaimer}</span>
            <button data-testid="ai-deepdive-refresh" onClick={() => run(true)}
              className="inline-flex items-center gap-1.5 mono text-[11px] text-muted-foreground hover:text-volt transition-colors duration-200">
              <ArrowClockwise size={13} /> Regenerate
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, cls = "", color }) {
  return <div><div className="label-xs">{label}</div><div className={`mono text-sm mt-1 ${cls}`} style={color ? { color } : {}}>{value}</div></div>;
}
function FactorList({ items, icon: Icon, color }) {
  return <ul className="space-y-1.5">{(items || []).map((f, i) => (
    <li key={i} className="flex items-start gap-2 text-xs"><Icon size={14} className={`mt-0.5 shrink-0 ${color}`} /><span className="text-muted-foreground">{f}</span></li>
  ))}</ul>;
}

function TradeSetup({ setup }) {
  return (
    <div className="hypo-bg border border-white/10 p-3 mb-4">
      <div className="flex items-center justify-between">
        <span className={`mono text-xs uppercase ${setup.side === "long" ? "pos" : "neg"}`}>{setup.side}</span>
        <span className="mono text-xs">R:R {setup.risk_reward}</span>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-center mono text-sm">
        <div><div className="label-xs">Entry</div>${setup.entry}</div>
        <div><div className="label-xs">Stop</div><span className="neg">${setup.stop_loss}</span></div>
        <div><div className="label-xs">Target</div><span className="pos">${setup.target}</span></div>
      </div>
    </div>
  );
}

function OrderTicket({ ticker, price, onDone }) {
  const isCrypto = ticker.includes("-USD");
  const unitWord = isCrypto ? "coins" : "shares";
  const [mode, setMode] = useState("usd"); // "usd" | "qty"
  const [qty, setQty] = useState(10);
  const [usd, setUsd] = useState(1000);
  const [cash, setCash] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/portfolio").then((r) => setCash(r.data.cash)).catch(() => {}); }, []);

  const computedQty = mode === "usd" ? (price > 0 ? Number(usd) / price : 0) : Number(qty);
  const estValue = (computedQty || 0) * price;

  const pickAmount = (v) => { setMode("usd"); setUsd(v); };

  const order = async (side) => {
    if (!(computedQty > 0)) return toast.error("Enter a valid amount");
    if (mode === "usd" && !(price > 0)) return toast.error("Live price unavailable — try again in a moment");
    setBusy(true);
    try {
      const quantity = mode === "usd" ? Number(computedQty.toFixed(6)) : Number(qty);
      await api.post("/orders", { ticker, side, quantity, order_type: "market" });
      toast.success(`Paper ${side} ${quantity} ${ticker} @ ~$${price}`);
      api.get("/portfolio").then((r) => setCash(r.data.cash)).catch(() => {});
      onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="hypo-bg border border-white/10 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="label-xs italic">Paper Order Ticket · simulated</div>
        <div className="flex rounded-sm overflow-hidden border border-border h-6" data-testid="order-mode-toggle">
          {["usd", "qty"].map((m) => (
            <button key={m} data-testid={`order-mode-${m}`} onClick={() => setMode(m)}
              className={`mono text-[10px] uppercase px-2 transition-colors duration-150 ${mode === m ? "bg-volt/20 text-volt" : "text-muted-foreground hover:text-white"}`}>
              {m === "usd" ? "$ Amount" : "Qty"}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {mode === "usd" ? (
          <div className="relative">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 mono text-sm text-muted-foreground">$</span>
            <Input data-testid="order-usd" type="number" min="1" step="any" value={usd} onChange={(e) => setUsd(e.target.value)} className="h-9 bg-background rounded-sm mono w-32 pl-6" />
          </div>
        ) : (
          <Input data-testid="order-qty" type="number" min="0" step="any" value={qty} onChange={(e) => setQty(e.target.value)} className="h-9 bg-background rounded-sm mono w-24" />
        )}
        <div className="mono text-xs text-muted-foreground" data-testid="order-estimate">
          {mode === "usd"
            ? `≈ ${(computedQty || 0).toLocaleString(undefined, { maximumFractionDigits: isCrypto ? 6 : 4 })} ${unitWord}`
            : `≈ $${estValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
        </div>
      </div>
      <div className="mt-2">
        <AmountChips cash={cash} onPick={pickAmount} testid="order-chips" />
        {cash != null && <div className="mono text-[10px] text-muted-foreground mt-1">Buying power ${cash.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <Button data-testid="buy-btn" disabled={busy} onClick={() => order("buy")} className="bg-signal-green hover:bg-signal-green/90 text-black rounded-sm h-9 font-semibold">Buy</Button>
        <Button data-testid="sell-btn" disabled={busy} onClick={() => order("sell")} className="bg-signal-red hover:bg-signal-red/90 text-white rounded-sm h-9 font-semibold">Sell</Button>
      </div>
    </div>
  );
}
